/**
 * PersonneAddForm and its NameFormChips helper for the Cibles personne category.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  TARGET_LIMITS,
  PERSON_TITLES,
  PERSON_TITLE_LANG_LABELS,
  generatePersonNames,
  getPersonTitleDef,
} from '@traaker/shared';
import { type TargetRow, type TagRegistry, tagNamesOf, CAT_BY_ID } from './CATEGORIES';
import { ChipListEditor } from './CatalogueViews';

// ---------------------------------------------------------------------------
// NameFormChips — shared by PersonneAddForm sections
// ---------------------------------------------------------------------------

/** Renders a row of clickable name-form chips. Shared by all three sections. */
export function NameFormChips({
  forms, activeNames, onToggle, color,
}: {
  forms: import('@traaker/shared').PersonNameForm[];
  activeNames: Set<string>;
  onToggle: (form: string, core?: string) => void;
  color: string;
}) {
  if (!forms.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625 }}>
      {forms.map(({ form, core }) => {
        const on = activeNames.has(form);
        return (
          <Box
            key={form}
            onClick={() => onToggle(form, core)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.625,
              px: 1.125, py: 0.375, borderRadius: 1, cursor: 'pointer',
              border: '1px solid',
              borderColor: on ? color : 'divider',
              bgcolor: on ? alpha(color, 0.08) : alpha('#000', 0.02),
              color: on ? color : 'text.secondary',
              fontSize: '0.8rem', fontWeight: on ? 600 : 400,
              userSelect: 'none', transition: 'all 0.1s',
              '&:hover': { borderColor: color, color },
            }}
          >
            {on && (
              <Box component="span" sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 5.2l2.5 2.5 4.5-5"/></svg>
              </Box>
            )}
            {form}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PersonneAddForm
// ---------------------------------------------------------------------------

/**
 * Builds the accepted name combinations for a person and lets the user toggle each one.
 * Checked entries become `alias` field values that compile to `text` signals.
 * `title`, `firstName`, `lastName` are stored as building-block fields (no direct signal).
 */
export function PersonneAddForm({
  open, mode = 'add', initialRow, marques, onAdd, onClose, saving, tagRegistry, workingLang,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  initialRow?: TargetRow;
  marques: TargetRow[];
  onAdd: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => void;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
  /** Brand working language — used to search titles and display language label in title picker. */
  workingLang: string;
}) {
  const { t } = useTranslation();
  const [parentId, setParentId] = useState('');
  const [titleCode, setTitleCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [titleSearch, setTitleSearch] = useState('');
  const [titleDropOpen, setTitleDropOpen] = useState(false);
  const titleSearchRef = useRef<HTMLInputElement>(null);

  /** Set of form strings that are ACTIVE (will become alias signals). */
  const [activeNames, setActiveNames] = useState<Set<string>>(new Set());

  const isEdit = mode === 'edit';

  // Tracks whether the name-change effect has seen its first value after the form opened.
  // False → first run after open = just record, don't reset (respects pre-filled edit values).
  // True  → subsequent run = user actually changed the name → full reset.
  const nameInitializedRef = useRef(false);

  // Pre-fill on open
  useEffect(() => {
    if (!open) return;
    nameInitializedRef.current = false; // arm the "skip first run" guard
    if (isEdit && initialRow) {
      setParentId(initialRow.parentTargetId ?? '');
      setTitleCode(initialRow.fields.find((f) => f.name === 'title')?.value as string ?? '');
      setFirstName(initialRow.fields.find((f) => f.name === 'firstName')?.value as string ?? '');
      setLastName(initialRow.fields.find((f) => f.name === 'lastName')?.value as string ?? '');
      const existingAliases = new Set(
        initialRow.fields.filter((f) => f.name === 'alias').map((f) => String(f.value)).filter(Boolean)
      );
      setActiveNames(existingAliases);
      setTags(tagNamesOf(initialRow, tagRegistry));
    } else {
      setParentId(marques.length === 1 ? marques[0].targetId : '');
      setTitleCode('');
      setFirstName('');
      setLastName('');
      setActiveNames(new Set());
      setTags([]);
    }
    setTitleSearch('');
    setTitleDropOpen(false);
  }, [open]);

  // Regenerate names when title/firstName/lastName change.
  const titleDef = getPersonTitleDef(titleCode) ?? null;
  const allForms = generatePersonNames(titleDef, firstName, lastName);

  // Sections for display
  const sansTitreForms  = allForms.filter((f) => f.lang === '');
  const workingLangForms = allForms.filter((f) => f.lang === workingLang);
  const otherLangForms   = allForms.filter((f) => f.lang !== '' && f.lang !== workingLang);

  // Collapse state for the two optional sections
  const [showTitleForms, setShowTitleForms] = useState(false);
  const [showAllLangForms, setShowAllLangForms] = useState(false);

  // When title CHANGES, remove ALL titled forms (every language of the previous title)
  // so they don't accumulate. Sans-titre forms (the name itself) are kept — the person
  // hasn't changed, only the way they're cited with a title.
  const prevTitleCodeRef = useRef(titleCode);
  useEffect(() => {
    const prev = prevTitleCodeRef.current;
    prevTitleCodeRef.current = titleCode;
    if (prev === titleCode) return;
    // Remove any form that was produced by ANY title (not just the previous one) — simpler
    // and future-proof: keep only the forms that are in the current generation's lang='' group.
    setActiveNames((p) => {
      const sansTitre = new Set(
        generatePersonNames(null, firstName, lastName).map((f) => f.form),
      );
      const next = new Set<string>();
      for (const name of p) {
        if (sansTitre.has(name)) next.add(name);
      }
      return next;
    });
    setShowTitleForms(false);
    setShowAllLangForms(false);
  }, [titleCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // When firstName or lastName changes: full reset to default sans-titre forms.
  // First run after open is skipped (nameInitializedRef guard) so pre-filled edit
  // values are respected. Every subsequent change is a real user edit → reboot.
  const prevSansTitreRef = useRef<string>('');
  useEffect(() => {
    const key = sansTitreForms.map((f) => f.form).join('|');
    if (key === prevSansTitreRef.current) return;
    const wasInitialized = nameInitializedRef.current;
    nameInitializedRef.current = true;
    prevSansTitreRef.current = key;
    if (!wasInitialized) return; // first run after open — don't reset
    // Name changed → reboot: clear everything and set only the defaultOn sans-titre forms.
    setActiveNames(new Set(sansTitreForms.filter((f) => f.defaultOn).map((f) => f.form)));
    setShowTitleForms(false);
    setShowAllLangForms(false);
  }, [sansTitreForms.map((f) => f.form).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Toggle a form. When checking a titled form, auto-uncheck its `core` (the
   *  corresponding no-title form that becomes redundant). */
  const toggleName = (form: string, core?: string) => {
    setActiveNames((prev) => {
      const next = new Set(prev);
      if (next.has(form)) {
        next.delete(form);
      } else {
        next.add(form);
        if (core) next.delete(core); // uncheck the less-specific form it supersedes
      }
      return next;
    });
  };

  // Title search: match against all forms of every title in the working language (+ EN fallback)
  const searchLangs = [workingLang, 'en', 'fr'].filter((v, i, a) => a.indexOf(v) === i);
  const titleMatches = titleSearch.trim().length < 1
    ? PERSON_TITLES
    : PERSON_TITLES.filter((td) => {
        const q = titleSearch.trim().toLowerCase();
        for (const lang of searchLangs) {
          const lf = td.forms[lang];
          if (!lf) continue;
          for (const f of [...lf.short, ...(lf.long ?? [])]) {
            if (f.toLowerCase().includes(q)) return true;
          }
        }
        // Also match code and emoji
        return td.code.toLowerCase().includes(q);
      });

  const buildLabel = (): string => {
    const parts = [titleCode ? `${titleCode} ` : '', firstName ? `${firstName} ` : '', lastName].join('').trim();
    return parts || t('cibles.unnamed');
  };

  const handleSave = () => {
    if (!lastName.trim()) return;
    const fields: { name: string; value: string }[] = [];
    if (titleCode) fields.push({ name: 'title', value: titleCode });
    if (firstName.trim()) fields.push({ name: 'firstName', value: firstName.trim() });
    fields.push({ name: 'lastName', value: lastName.trim() });
    for (const form of activeNames) {
      fields.push({ name: 'alias', value: form });
    }
    onAdd({ label: buildLabel(), parentTargetId: parentId || undefined, fields, tags });
  };

  const cat = CAT_BY_ID['personne'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
            {isEdit ? t('cibles.personne.edit_title') : t('cibles.add.personne')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {t('cibles.personne.subtitle')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}
          sx={{ mt: -0.5, mr: -0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Brand (optional) */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.parent_column')}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400, fontSize: '0.75rem' }}>
              {t('cibles.col_optional')}
            </Typography>
          </Typography>
          <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
            renderValue={(value) => {
              const brand = marques.find((m) => m.targetId === value);
              if (!brand) return <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>{t('cibles.parent_none')}</Typography>;
              return (
                <Box sx={{ display: 'inline-flex', bgcolor: alpha(cat.color, 0.1), color: alpha(cat.color, 0.9), fontWeight: 700, fontSize: '0.8rem', px: 1, py: 0.3, borderRadius: 0.75 }}>
                  {brand.label}
                </Box>
              );
            }}
            sx={{ fontSize: '0.8125rem' }}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.parent_none')}</MenuItem>
            {marques.map((m) => (
              <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: '0.8125rem' }}>{m.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Title + First name + Last name */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 1.5 }}>
          {/* Title — searchable picker */}
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.personne.title_label')}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                {t('cibles.col_optional')}
              </Typography>
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box
                onClick={() => { setTitleDropOpen((p) => !p); setTimeout(() => titleSearchRef.current?.focus(), 50); }}
                sx={{
                  height: 40, px: 1.25, border: '1px solid', borderColor: titleDropOpen ? cat.color : 'divider',
                  borderRadius: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                  bgcolor: titleDropOpen ? alpha(cat.color, 0.03) : 'transparent',
                  boxShadow: titleDropOpen ? `0 0 0 3px ${alpha(cat.color, 0.12)}` : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                {titleDef ? (
                  <>
                    <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}>{titleDef.emoji}</Typography>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {titleDef.forms[workingLang]?.short[0] ?? titleDef.forms['en']?.short[0] ?? titleDef.code}
                    </Typography>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setTitleCode(''); setTitleSearch(''); }}
                      sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled', flex: 1 }}>
                    {t('cibles.personne.title_none')}
                  </Typography>
                )}
              </Box>
              {titleDropOpen && (
                <Box sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 'auto', minWidth: 260, zIndex: 600, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden' }}>
                  <Box sx={{ px: 1.25, py: 0.875, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <SearchIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                    <Box
                      component="input"
                      ref={titleSearchRef}
                      value={titleSearch}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleSearch(e.target.value)}
                      placeholder={t('cibles.personne.title_search')}
                      sx={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.8125rem', fontFamily: 'inherit', bgcolor: 'transparent', color: 'text.primary', '&::placeholder': { color: 'text.disabled' } }}
                    />
                  </Box>
                  <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                    {titleMatches.length === 0 ? (
                      <Typography sx={{ px: 1.5, py: 1, fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.personne.title_no_result')}</Typography>
                    ) : titleMatches.map((td) => {
                      const langForms = td.forms[workingLang] ?? td.forms['en'] ?? td.forms[Object.keys(td.forms)[0]];
                      const displayLabel = langForms ? [...langForms.short, ...(langForms.long ?? [])].slice(0, 2).join(' / ') : td.code;
                      return (
                        <Box key={td.code}
                          onClick={() => { setTitleCode(td.code); setTitleDropOpen(false); setTitleSearch(''); }}
                          sx={{ px: 1.5, py: 0.875, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: alpha('#000', 0.04),
                            bgcolor: td.code === titleCode ? alpha(cat.color, 0.07) : 'transparent', '&:hover': { bgcolor: alpha(cat.color, 0.05) }, '&:last-child': { borderBottom: 'none' } }}>
                          <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1, width: 22, flexShrink: 0 }}>{td.emoji}</Typography>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: td.code === titleCode ? 600 : 400, flex: 1, whiteSpace: 'nowrap' }}>{displayLabel}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.personne.firstName_label')}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                {t('cibles.col_optional')}
              </Typography>
            </Typography>
            <TextField size="small" fullWidth value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('cibles.personne.firstName_ph')}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }} />
          </Box>

          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.personne.lastName_label')}
            </Typography>
            <TextField size="small" fullWidth value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder={t('cibles.personne.lastName_ph')}
              error={!lastName.trim() && lastName !== ''}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }} />
          </Box>
        </Box>

        {/* Name forms — three sections */}
        {lastName.trim() && (
          <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.25 }}>

            {/* Section 1: Sans titre — always visible */}
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t('cibles.personne.no_title_group')}
              </Typography>
              <NameFormChips forms={sansTitreForms} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
            </Box>

            {/* Section 2: Working-language title forms (collapsed by default) */}
            {titleDef && workingLangForms.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box
                  onClick={() => setShowTitleForms((p) => !p)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                    bgcolor: showTitleForms ? alpha(cat.color, 0.05) : 'transparent',
                    '&:hover': { bgcolor: alpha(cat.color, 0.04) } }}
                >
                  <Typography component="span" sx={{ fontSize: '0.875rem', lineHeight: 1, flexShrink: 0 }}>{titleDef.emoji}</Typography>
                  <Typography sx={{ flex: 1, fontSize: '0.8rem', fontWeight: 500 }}>
                    {t('cibles.personne.title_filter_label')}
                  </Typography>
                  {activeNames.size > sansTitreForms.filter(f => activeNames.has(f.form)).length && (
                    <Box sx={{ bgcolor: alpha(cat.color, 0.12), color: cat.color, fontWeight: 700, fontSize: '0.7rem', px: 0.75, py: 0.15, borderRadius: 0.75 }}>
                      {allForms.filter(f => f.lang !== '' && activeNames.has(f.form)).length}
                    </Box>
                  )}
                  <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', transform: showTitleForms ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>▶</Box>
                </Box>
                {showTitleForms && (
                  <Box sx={{ px: 1.5, pb: 1.25, pt: 0.5, borderTop: '1px solid', borderColor: alpha('#000', 0.06) }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: '0.72rem', color: 'text.disabled' }}>
                      {t('cibles.personne.title_filter_hint', { lang: PERSON_TITLE_LANG_LABELS[workingLang] ?? workingLang })}
                    </Typography>
                    <NameFormChips forms={workingLangForms} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
                  </Box>
                )}
              </Box>
            )}

            {/* Section 3: Other languages (collapsed by default) */}
            {titleDef && otherLangForms.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box
                  onClick={() => setShowAllLangForms((p) => !p)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                    bgcolor: showAllLangForms ? alpha(cat.color, 0.05) : 'transparent',
                    '&:hover': { bgcolor: alpha(cat.color, 0.04) } }}
                >
                  <Typography sx={{ flex: 1, fontSize: '0.8rem', fontWeight: 500 }}>
                    {t('cibles.personne.all_langs_label')}
                  </Typography>
                  {allForms.filter(f => f.lang !== '' && f.lang !== workingLang && activeNames.has(f.form)).length > 0 && (
                    <Box sx={{ bgcolor: alpha(cat.color, 0.12), color: cat.color, fontWeight: 700, fontSize: '0.7rem', px: 0.75, py: 0.15, borderRadius: 0.75 }}>
                      {allForms.filter(f => f.lang !== '' && f.lang !== workingLang && activeNames.has(f.form)).length}
                    </Box>
                  )}
                  <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', transform: showAllLangForms ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>▶</Box>
                </Box>
                {showAllLangForms && (
                  <Box sx={{ px: 1.5, pb: 1.25, pt: 0.5, borderTop: '1px solid', borderColor: alpha('#000', 0.06), display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {/* Group by language */}
                    {[...new Set(otherLangForms.map(f => f.lang))].sort().map(lang => (
                      <Box key={lang}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          {PERSON_TITLE_LANG_LABELS[lang] ?? lang}
                        </Typography>
                        <NameFormChips forms={otherLangForms.filter(f => f.lang === lang)} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, px: 1.25, py: 0.875, borderRadius: 1.25, bgcolor: alpha(cat.color, 0.06), color: alpha(cat.color, 0.9) }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><circle cx="5.4" cy="10.6" r="3"/><path d="M7.5 8.5 13.2 2.8M11 5l1.7 1.7M9.4 6.6l1.3 1.3"/></svg>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                <strong>{activeNames.size}</strong>{' '}
                {t('cibles.personne.names_summary', { total: allForms.length })}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.72rem', color: 'text.disabled' }}>
              {t('cibles.personne.names_fold_hint')}
            </Typography>
          </Box>
        )}

        {/* Tags */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.tags.label')}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
              {t('cibles.col_optional')}
            </Typography>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.875, color: 'text.disabled', fontSize: '0.72rem' }}>{t('cibles.tags.hint')}</Typography>
          <ChipListEditor
            values={tags}
            onChange={setTags}
            max={TARGET_LIMITS.maxTagsPerTarget}
            maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')}
            suggestions={[...tagRegistry.values()]}
            disabled={saving}
            hex={cat.color}
          />
        </Box>

      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving}
          sx={{ fontSize: '0.8125rem', borderRadius: 1.25 }}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave}
          disabled={saving || !lastName.trim()}
          sx={{ fontSize: '0.8125rem', borderRadius: 1.25, bgcolor: cat.color, '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </Box>
    </Dialog>
  );
}
